import { isEmpty, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { getChargePostings } from '../accounting/chargesUtils';
import { contactMostImportantType, getContactNameFromObject, getSortableContactName } from '../contact/contactutils';
import { EntityType, getTranslatedCategory } from '../fileUtils';
import { allLanguageBackendMessages } from '../i18n/backendMessages';
import { getTypedLanguage } from '../i18nutils';
import { getEntityLabels } from '../labelsUtils';
import { isNilOrEmpty } from '../objectUtils';
import {
  Building,
  Charge,
  ChargeDTO,
  Contact,
  ContactType,
  CustomLabel,
  FileCategory,
  Invoice,
  Language,
  Lease,
  Technic,
  Unit,
  UnitLease,
  UnitOwner,
} from '../types';

import {
  DocumentCategoryWithoutReadIdAndClientId,
  DocumentModel,
  FolderEntity,
  FolderModel,
  ROOT_FOLDER_ID,
  ROOT_PARENT_FOLDER_ID,
  checkChildFolders,
  getCategoriesToWorkWith,
  getMappedS3File,
} from './folderUtils';

export interface ChargesMap {
  entityId?: string;
  documentId: string;
}

export const basicCreateFolderObject: Partial<FolderModel> = {
  id: undefined,
  size: 0,
  updatedAt: '',
  documentModel: undefined,
};

interface BuildingProps {
  buildingId: string;
  buildingName: string;
  buildingCategory: DocumentCategoryWithoutReadIdAndClientId;
  buildingFolderId: string;
}

interface UnitProps {
  unitLease: UnitLease;
  unitCategory: DocumentCategoryWithoutReadIdAndClientId;
  unitId: string;
  unitFolderId: string;
  unitName: string;
}

export const BASE_CONTACT_ENTITY_ID = 'contacts';

export const setFolderList = (
  allFoldersToAdd: FolderModel[],
  docsToWorkOn: DocumentModel[],
  fileCategoriesToWorkWith: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null,
  documentCategoryId?: string,
  depthId?: string,
  depthString?: string,
  folderDepthByFolderId?: string[],
  depthStringForDB?: (string | CustomLabel[])[],
  parentFolderId?: string,
  initialCatId?: string,
  databaseDepthString?: string,
  entityId?: string
): FolderModel[] | undefined => {
  const currentCategory = fileCategoriesToWorkWith?.find((category) => category.id === documentCategoryId);
  const currentDocs = docsToWorkOn.filter((doc) => {
    return doc.category?.id === documentCategoryId;
  });

  const currentLevelCategories = fileCategoriesToWorkWith.filter(
    (category) => category.parentId === (isNil(documentCategoryId) ? null : documentCategoryId)
  );

  const foldersToAdd: FolderModel[] = [];
  currentDocs.forEach((doc) => {
    doc.category = {
      ...doc.category!,
      parentId: currentCategory?.parentId,
    };
    const createdFolder: FolderModel = {
      ...basicCreateFolderObject,
      id: uuidv4(),
      folderEntity: FolderEntity.FILE,
      size: doc.size ?? 0,
      updatedAt: doc.updatedAt ?? '',
      translatedLabel: doc.fileName,
      documentModel: doc,
      documentCategory: currentCategory!,
      folderDepth: depthId ?? 'rootFolder',
      folderDepthByFolderId: folderDepthByFolderId ?? [],
      folderDepthString: depthString ?? '/',
      folderDepthStringForDB: depthStringForDB ?? [],
      databaseDepthString: databaseDepthString ?? ROOT_FOLDER_ID,
      parentFolderId: parentFolderId ?? ROOT_PARENT_FOLDER_ID,
      entityId,
    };

    foldersToAdd.push(createdFolder);
  });

  currentLevelCategories.forEach((category) => {
    if (!checkChildFolders(docsToWorkOn, fileCategoriesToWorkWith, category.id)) return;
    const folderId = uuidv4();
    foldersToAdd.push({
      ...basicCreateFolderObject,
      id: folderId,
      folderEntity: FolderEntity.FOLDER,
      translatedLabel: getTranslatedCategory(category, getTypedLanguage(language)),
      documentCategory: category,
      folderDepth: depthId ?? 'rootFolder',
      folderDepthString: depthString ?? '/',
      folderDepthByFolderId: folderDepthByFolderId ?? [],
      folderDepthStringForDB: depthStringForDB ?? [],
      parentFolderId: parentFolderId ?? ROOT_PARENT_FOLDER_ID,
      databaseDepthString: databaseDepthString ?? ROOT_FOLDER_ID,
      entityId,
    } as FolderModel);

    setFolderList(
      allFoldersToAdd,
      docsToWorkOn,
      fileCategoriesToWorkWith,
      language,
      category!.id,
      depthId + '.' + category.id,
      (depthString ?? '') + '/' + getTranslatedCategory(category, getTypedLanguage(language)),
      [...(folderDepthByFolderId ?? []), folderId],
      [...(depthStringForDB ?? []), category.labels],
      parentFolderId,
      initialCatId,
      databaseDepthString ? `${databaseDepthString}.${category.id}` : undefined,
      entityId
    );
  });
  allFoldersToAdd.push(...foldersToAdd);
  if (depthId === 'rootFolder' || initialCatId === documentCategoryId) {
    return allFoldersToAdd;
  }
  return undefined;
};

export const calculateTechnics = (id: string, technics: Technic[], files: DocumentModel[]): DocumentModel[] => {
  const technicFiles: DocumentModel[] = [];
  const technicsBuilding = getTechnicsFor(id, technics);
  if (!isNilOrEmpty(technicsBuilding)) {
    technicsBuilding.forEach((technic) => {
      technicFiles.push(...files.filter((file) => file.foreignKey === technic.id));
    });
  }
  return technicFiles;
};

const calculateBuildingsFolders = (
  building: Building,
  buildingCategories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null,
  technics: Technic[],
  charges: ChargesMap[],
  files: { [key: string]: DocumentModel[] }
): {
  folders: FolderModel[];
  entityCategory: DocumentCategoryWithoutReadIdAndClientId;
  buildingFolderId: string;
} => {
  const folders: FolderModel[] = [];
  const tempBuildingCategoryId = uuidv4();
  const tempBuildingCategory: DocumentCategoryWithoutReadIdAndClientId = {
    id: tempBuildingCategoryId,
    labels: getEntityLabels(building.name),
    parentId: null,
    fileCategory: FileCategory.CUSTOM,
    entity: EntityType.BUILDING,
  };
  const folderId = uuidv4();
  const folder: FolderModel = {
    ...basicCreateFolderObject,
    id: folderId,
    folderEntity: FolderEntity.BUILDING,
    translatedLabel: building.name,
    documentCategory: tempBuildingCategory,
    folderDepth: 'rootFolder',
    folderDepthByFolderId: [],
    folderDepthString: '/',
    folderDepthStringForDB: [],
    databaseDepthString: ROOT_FOLDER_ID,
    entityId: building.id,
    parentFolderId: folderId,
  } as FolderModel;
  folders.push(folder);
  const currentBuildingFiles = files[EntityType.BUILDING].reduce<DocumentModel[]>((acc, file) => {
    if (file.foreignKey === building.id) {
      if (isNil(file.category?.parentId)) {
        file.category!.parentId = tempBuildingCategoryId;
      }
      acc.push(getMappedS3File(file, getTypedLanguage(language)));
    }
    return acc;
  }, []);

  const technicsBuilding = calculateTechnics(building.id, technics, files[EntityType.TECHNIC]);
  if (!isEmpty(technicsBuilding)) {
    currentBuildingFiles.push(...technicsBuilding);
  }
  if (!isEmpty(charges)) {
    const filesToAdd = charges.reduce((acc, charge) => {
      if (charge.entityId === building.id) {
        const currentFile = files[EntityType.CHARGE].find((file) => file.id === charge.documentId);
        if (!isNil(currentFile)) {
          acc.push(currentFile);
        }
      }
      return acc;
    }, [] as DocumentModel[]);
    if (!isEmpty(filesToAdd)) {
      currentBuildingFiles.push(...filesToAdd);
    }
  }

  if (!isEmpty(currentBuildingFiles)) {
    const categoriesToWorkWith = getCategoriesToWorkWith(buildingCategories, tempBuildingCategoryId);
    categoriesToWorkWith.push(tempBuildingCategory);
    const buildingFoldersToAdd = setFolderList(
      [],
      currentBuildingFiles,
      categoriesToWorkWith,
      language,
      tempBuildingCategoryId,
      `rootFolder.${tempBuildingCategoryId}`,
      `/${building.name}`,
      [folderId],
      [building.name],
      folder.parentFolderId,
      tempBuildingCategoryId,
      `${ROOT_FOLDER_ID}.${building.id}`,
      building.id
    );
    if (!isNil(buildingFoldersToAdd)) {
      folders.push(...buildingFoldersToAdd);
    }
  }
  return { folders, entityCategory: tempBuildingCategory, buildingFolderId: folderId };
};

const calculateUnitFolders = (
  unit: Unit,
  buildingProps: BuildingProps,
  unitCategories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null,
  technics: Technic[],
  charges: ChargesMap[],
  files: { [key: string]: DocumentModel[] }
): {
  unitFolders: FolderModel[];
  entityCategory: DocumentCategoryWithoutReadIdAndClientId;
  unitFolderId: string;
} => {
  const { buildingId, buildingName, buildingFolderId, buildingCategory } = buildingProps;
  const unitFolders: FolderModel[] = [];
  const tempUnitCategoryId = uuidv4();
  const unitDocumentCategory: DocumentCategoryWithoutReadIdAndClientId = {
    id: tempUnitCategoryId,
    labels: getEntityLabels(unit.name),
    parentId: buildingCategory.id,
    fileCategory: FileCategory.CUSTOM,
    entity: EntityType.UNIT,
  };
  const unitFolderId = uuidv4();
  const unitFolder: FolderModel = {
    ...basicCreateFolderObject,
    id: unitFolderId,
    folderEntity: FolderEntity.UNIT,
    translatedLabel: unit.name,
    documentCategory: unitDocumentCategory,
    folderDepth: `rootFolder.${buildingCategory.id}`,
    folderDepthByFolderId: [buildingFolderId],
    folderDepthString: `/${buildingName}`,
    folderDepthStringForDB: [buildingName],
    databaseDepthString: `${ROOT_FOLDER_ID}.${buildingId}`,
    entityId: unit.id,
    parentFolderId: unitFolderId,
  } as FolderModel;
  unitFolders.push(unitFolder);
  const currentUnitFiles = files[EntityType.UNIT].reduce<DocumentModel[]>((acc, file) => {
    if (file.foreignKey === unit.id) {
      acc.push(getMappedS3File(file, getTypedLanguage(language)));
    }
    return acc;
  }, []);

  const technicsUnit = calculateTechnics(unit.id, technics, files[EntityType.TECHNIC]);
  if (!isEmpty(technicsUnit)) {
    currentUnitFiles.push(...technicsUnit);
  }

  if (!isEmpty(charges)) {
    const filesToAdd = charges.reduce((acc, charge) => {
      if (charge.entityId === unit.id) {
        const currentFile = files[EntityType.CHARGE].find((file) => file.id === charge.documentId);
        if (!isNil(currentFile)) {
          acc.push(currentFile);
        }
      }
      return acc;
    }, [] as DocumentModel[]);
    if (!isEmpty(filesToAdd)) {
      currentUnitFiles.push(...filesToAdd);
    }
  }

  if (!isEmpty(currentUnitFiles)) {
    const categoriesToWorkWith = getCategoriesToWorkWith(unitCategories, tempUnitCategoryId);
    categoriesToWorkWith.push(unitDocumentCategory);
    const unitFoldersToAdd = setFolderList(
      [],
      currentUnitFiles,
      categoriesToWorkWith,
      language,
      tempUnitCategoryId,
      `rootFolder.${buildingCategory.id}.${tempUnitCategoryId}`,
      `/${buildingName}/${unit.name}`,
      [buildingFolderId, unitFolderId],
      [buildingName, unit.name],
      unitFolder.parentFolderId,
      tempUnitCategoryId,
      `${ROOT_FOLDER_ID}.${buildingId}.${unit.id}`,
      unit.id
    );
    if (!isNil(unitFoldersToAdd)) {
      unitFolders.push(...unitFoldersToAdd);
    }
  }

  return { unitFolders, entityCategory: unitDocumentCategory, unitFolderId };
};

export const calculateInvoices = (invoices: Invoice[], allInvoicesFiles: DocumentModel[], leaseId: string) => {
  const leaseInvoices = invoices.filter((invoice) => invoice.leaseId === leaseId);

  const invoiceFilesOfLease = leaseInvoices.flatMap((invoice) =>
    allInvoicesFiles.reduce<DocumentModel[]>((fileAcc, file) => {
      if (file.foreignKey === invoice.id) {
        fileAcc.push({
          ...file,
          invoicePaid: invoice.paid,
        });
      }
      return fileAcc;
    }, [])
  );
  return invoiceFilesOfLease;
};

const calculateLeaseFolders = (
  lease: Lease,
  unitProps: UnitProps,
  buildingProps: BuildingProps,
  leaseCategories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null,
  technics: Technic[],
  invoices: Invoice[],
  files: { [key: string]: DocumentModel[] }
): {
  leaseFolders: FolderModel[];
  leaseDocumentCategory: DocumentCategoryWithoutReadIdAndClientId;
} => {
  const { buildingCategory, buildingName, buildingId, buildingFolderId } = buildingProps;
  const { unitLease, unitCategory, unitId, unitFolderId, unitName } = unitProps;
  const leaseFolders: FolderModel[] = [];
  const tempLeaseCategoryId = uuidv4();
  const leaseDocumentCategory: DocumentCategoryWithoutReadIdAndClientId = {
    id: tempLeaseCategoryId,
    labels: getEntityLabels(lease.name),
    parentId: unitCategory.id,
    fileCategory: FileCategory.CUSTOM,
    entity: EntityType.LEASE,
  };
  const leaseFolderId = uuidv4();
  const leaseFolder: FolderModel = {
    ...basicCreateFolderObject,
    id: leaseFolderId,
    folderEntity: FolderEntity.LEASE,
    translatedLabel: lease.name,
    documentCategory: leaseDocumentCategory,
    folderDepth: `rootFolder.${buildingCategory.id}.${unitCategory.id}`,
    folderDepthByFolderId: [buildingFolderId, unitFolderId],
    folderDepthString: `/${buildingName}/${unitName}`,
    folderDepthStringForDB: [buildingName, unitName],
    databaseDepthString: `${ROOT_FOLDER_ID}.${buildingId}.${unitId}`,
    entityId: unitLease.leaseId,
    parentFolderId: leaseFolderId,
  } as FolderModel;
  leaseFolders.push(leaseFolder);
  const currentLeaseFiles = files[EntityType.LEASE].reduce<DocumentModel[]>((acc, file) => {
    if (file.foreignKey === lease?.id) {
      acc.push(getMappedS3File(file, getTypedLanguage(language)));
    }
    return acc;
  }, []);

  const technicsLease = calculateTechnics(lease.id, technics, files[EntityType.TECHNIC]);
  if (!isEmpty(technicsLease)) {
    currentLeaseFiles.push(...technicsLease);
  }

  const invoiceFiles = calculateInvoices(invoices, files[EntityType.INVOICE], lease.id);
  currentLeaseFiles.push(...invoiceFiles);

  if (!isEmpty(currentLeaseFiles)) {
    const categoriesToWorkWith = getCategoriesToWorkWith(leaseCategories, tempLeaseCategoryId);
    categoriesToWorkWith.push(leaseDocumentCategory);
    const leaseFoldersToAdd = setFolderList(
      [],
      currentLeaseFiles,
      categoriesToWorkWith,
      language,
      tempLeaseCategoryId,
      `rootFolder.${buildingCategory.id}.${unitCategory.id}.${tempLeaseCategoryId}`,
      `/${buildingName}/${unitName}/${lease.name}`,
      [buildingFolderId, unitFolderId, leaseFolderId],
      [buildingName, unitName, lease.name],
      leaseFolder.parentFolderId,
      tempLeaseCategoryId,
      `${ROOT_FOLDER_ID}.${buildingId}.${unitId}.${lease.id}`,
      unitLease.leaseId
    );
    if (!isNil(leaseFoldersToAdd)) {
      leaseFolders.push(...leaseFoldersToAdd);
    }
  }
  return { leaseFolders, leaseDocumentCategory };
};

const setJointFolders = (
  contactFolder: FolderModel,
  files: DocumentModel[],
  contactCategories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null,
  contactId: string,
  calculateFiles?: boolean,
  newCategories?: DocumentCategoryWithoutReadIdAndClientId[]
) => {
  const foldersToAdd: FolderModel[] = [];
  const currentContactFiles = files.reduce<DocumentModel[]>((contactFiles, file) => {
    if (file.foreignKey !== contactFolder.entityId) return contactFiles;
    contactFiles.push(getMappedS3File(file, getTypedLanguage(language)));
    return contactFiles;
  }, []);
  if (!isEmpty(currentContactFiles)) {
    const categoriesToSetFolderList = getCategoriesToWorkWith(
      isNilOrEmpty(newCategories) ? contactCategories : newCategories,
      contactFolder.documentCategory.id
    );

    const tempFoldersToAdd = setFolderList(
      [],
      currentContactFiles,
      categoriesToSetFolderList,
      language,
      contactFolder.documentCategory.id,
      calculateFiles
        ? `${contactFolder.folderDepth}.${contactFolder.documentCategory.id}`
        : `rootFolder.${contactFolder.documentCategory.id}`,
      calculateFiles
        ? `${contactFolder.folderDepthString}/${contactFolder.translatedLabel}`.replace('//', '/')
        : `/${contactFolder.translatedLabel}`,
      calculateFiles ? [...(contactFolder.folderDepthByFolderId ?? []), contactFolder.id] : [contactFolder.id],
      calculateFiles
        ? [...(contactFolder.folderDepthStringForDB ?? []), contactFolder.translatedLabel]
        : [contactFolder.documentCategory.labels],
      contactFolder.parentFolderId,
      contactFolder.documentCategory.id,
      calculateFiles ? `${contactFolder.databaseDepthString}.${contactId}` : ROOT_FOLDER_ID,
      contactId
    );
    if (!isNil(tempFoldersToAdd)) {
      foldersToAdd.push(...tempFoldersToAdd);
    }
  }
  return foldersToAdd;
};

const calculateJointOwnershipFolders = (
  contact: Contact,
  contacts: Contact[],
  contactFiles: DocumentModel[],
  contactCategories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null,
  parentFolder?: FolderModel,
  calculateFiles?: boolean,
  newCategories?: DocumentCategoryWithoutReadIdAndClientId[]
) => {
  // const files = getDocumentsListForEntity(EntityType.CONTACT);
  const jointOwnersContacts = contact.jointOwners?.map((jointOwner) =>
    contacts.find((stateContact) => stateContact.id === jointOwner.contactId)
  );

  if (isNil(jointOwnersContacts)) {
    return;
  }
  const folders = jointOwnersContacts.reduce<FolderModel[]>((acc, jointOwner) => {
    if (isNil(jointOwner)) {
      return acc;
    }

    const jointOwnerCategoryId = uuidv4();
    const contactCategory: DocumentCategoryWithoutReadIdAndClientId = {
      id: jointOwnerCategoryId,
      labels: getEntityLabels(getContactNameFromObject(jointOwner)),
      parentId: calculateFiles ? parentFolder?.documentCategory.id : null,
      fileCategory: FileCategory.CUSTOM,
      entity: EntityType.CONTACT,
    };
    const contactFolderId = uuidv4();
    const contactFolder: FolderModel = {
      ...basicCreateFolderObject,
      id: contactFolderId,
      folderEntity: FolderEntity.CONTACT,
      translatedLabel: getContactNameFromObject(jointOwner),
      documentCategory: contactCategory,
      folderDepth: calculateFiles ? `${parentFolder?.folderDepth}.${parentFolder?.documentCategory.id}` : 'rootFolder',
      folderDepthByFolderId: calculateFiles ? [...(parentFolder?.folderDepthByFolderId ?? []), parentFolder?.id] : [],
      folderDepthString: calculateFiles
        ? `${parentFolder?.folderDepthString}/${parentFolder?.translatedLabel}`.replace('//', '/')
        : '/',
      databaseDepthString: calculateFiles ? `${parentFolder?.databaseDepthString}.${contact.id}` : ROOT_FOLDER_ID,
      folderDepthStringForDB: calculateFiles
        ? [...(parentFolder?.folderDepthStringForDB ?? []), parentFolder?.translatedLabel]
        : [],
      entityId: jointOwner.id,
      labelToSortOn: getSortableContactName(jointOwner),
      parentFolderId: contactFolderId,
    } as FolderModel;
    acc.push(contactFolder);
    const jointFoldersToAdd = setJointFolders(
      contactFolder,
      contactFiles,
      contactCategories,
      language,
      jointOwner.id,
      calculateFiles,
      newCategories
    );
    if (!isEmpty(jointFoldersToAdd)) {
      acc.push(...jointFoldersToAdd);
    }
    return acc;
  }, []);
  return folders;
};

const calculateContactsFolders = (
  contacts: Contact[],
  contactClientFiles: DocumentModel[],
  contactCategories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null
): {
  folders: FolderModel[];
  entityCategories: DocumentCategoryWithoutReadIdAndClientId[];
} => {
  const folders: FolderModel[] = [];
  const entityCategories: DocumentCategoryWithoutReadIdAndClientId[] = [];
  const tempContactCategoryId = uuidv4();
  const labels = Object.entries(allLanguageBackendMessages).map(([lang, translations]) => ({
    language: lang as Language,
    label: translations.settings.rolesAndPermissions.sections.contacts,
  }));
  const tempContactCategory: DocumentCategoryWithoutReadIdAndClientId = {
    id: tempContactCategoryId,
    labels,
    parentId: null,
    fileCategory: FileCategory.CUSTOM,
    entity: EntityType.CONTACT,
  };
  const rootContactFolderId = uuidv4();
  const contactsFolder: FolderModel = {
    ...basicCreateFolderObject,
    id: rootContactFolderId,
    folderEntity: FolderEntity.CONTACT,
    translatedLabel: getTranslatedCategory(tempContactCategory, getTypedLanguage(language)),
    documentCategory: tempContactCategory,
    folderDepth: 'rootFolder',
    folderDepthString: '/',
    folderDepthByFolderId: [],
    folderDepthStringForDB: [tempContactCategory.labels],
    entityId: BASE_CONTACT_ENTITY_ID,
    databaseDepthString: ROOT_FOLDER_ID,
    parentFolderId: rootContactFolderId,
  } as FolderModel;

  folders.push(contactsFolder);
  entityCategories.push(tempContactCategory);
  const filteredContacts = contacts.filter((contact) =>
    contact.types.some((contactType) =>
      [
        ContactType.CONTRACTOR,
        ContactType.GUARANTOR,
        ContactType.JOINT_OWNERSHIP,
        ContactType.OWNER,
        ContactType.TENANT,
      ].includes(contactType)
    )
  );
  for (const contact of filteredContacts) {
    const tempFilteredContactCategoryId = uuidv4();
    const contactCategory: DocumentCategoryWithoutReadIdAndClientId = {
      id: tempFilteredContactCategoryId,
      labels: getEntityLabels(getContactNameFromObject(contact)),
      parentId: tempContactCategoryId,
      fileCategory: FileCategory.CUSTOM,
      entity: EntityType.CONTACT,
    };
    const contactFolderId = uuidv4();
    const contactFolder: FolderModel = {
      ...basicCreateFolderObject,
      id: contactFolderId,
      folderEntity: FolderEntity.CONTACT,
      translatedLabel: getContactNameFromObject(contact),
      documentCategory: contactCategory,
      folderDepth: `rootFolder.${tempContactCategoryId}`,
      folderDepthByFolderId: [rootContactFolderId],
      folderDepthString: `/${getTranslatedCategory(tempContactCategory, getTypedLanguage(language))}`,
      folderDepthStringForDB: [tempContactCategory.labels],
      databaseDepthString: `${ROOT_FOLDER_ID}.contacts`,
      entityId: contact.id,
      labelToSortOn: getSortableContactName(contact),
      parentFolderId: contactFolderId,
    } as FolderModel;
    folders.push(contactFolder);
    entityCategories.push(contactCategory);

    if (contactMostImportantType(contact) === ContactType.JOINT_OWNERSHIP) {
      const jointOwnershipFolders = calculateJointOwnershipFolders(
        contact,
        contacts,
        contactClientFiles,
        contactCategories,
        language,
        contactFolder,
        true
      );
      if (!isNil(jointOwnershipFolders)) {
        folders.push(...jointOwnershipFolders);
      }
    }

    const currentContactFiles = contactClientFiles.reduce<DocumentModel[]>((acc, file) => {
      if (file.foreignKey === contact.id) {
        acc.push(getMappedS3File(file, getTypedLanguage(language)));
      }
      return acc;
    }, []);

    if (!isEmpty(currentContactFiles)) {
      const categoriesToWorkWith = getCategoriesToWorkWith(contactCategories, tempFilteredContactCategoryId);
      categoriesToWorkWith.push(contactCategory);
      const contactFoldersToAdd = setFolderList(
        [],
        currentContactFiles,
        categoriesToWorkWith,
        language,
        tempFilteredContactCategoryId,
        `rootFolder.${tempContactCategoryId}.${tempFilteredContactCategoryId}`,
        `/${getTranslatedCategory(tempContactCategory, getTypedLanguage(language))}/${getContactNameFromObject(
          contact
        )}`,
        [rootContactFolderId, contactFolderId],
        [tempContactCategory.labels, getContactNameFromObject(contact)],
        contactFolder.parentFolderId,
        tempFilteredContactCategoryId,
        `${ROOT_FOLDER_ID}.contacts.${contact.id}`,
        contact.id
      );
      if (!isNil(contactFoldersToAdd)) {
        folders.push(...contactFoldersToAdd);
      }
    }
  }
  return { folders, entityCategories };
};

export const calculateAllFolders = (
  language: string | null,
  contacts: Contact[],
  buildings: Building[],
  units: Unit[],
  leases: Lease[],
  technics: Technic[],
  invoices: Invoice[],
  charges: Charge[],
  unitOwners: UnitOwner[],
  allCategories: { [key: string]: DocumentCategoryWithoutReadIdAndClientId[] },
  s3Files: { [key: string]: DocumentModel[] }
): {
  folders: FolderModel[];
  entityCategories: DocumentCategoryWithoutReadIdAndClientId[];
} => {
  const folders: FolderModel[] = [];
  const entityCategories: DocumentCategoryWithoutReadIdAndClientId[] = [];
  const { buildingCharges, unitCharges } = calculateUniqueCharges(charges, unitOwners);

  const { folders: contactFolders, entityCategories: newContactCategories } = calculateContactsFolders(
    contacts,
    s3Files[EntityType.CONTACT],
    allCategories[EntityType.CONTACT],
    language
  );
  folders.push(...contactFolders);
  entityCategories.push(...newContactCategories);

  for (const building of buildings) {
    const {
      folders: buildingFolders,
      entityCategory: buildingCategory,
      buildingFolderId,
    } = calculateBuildingsFolders(
      building,
      allCategories[EntityType.BUILDING],
      language,
      technics,
      buildingCharges,
      s3Files
    );
    entityCategories.push(buildingCategory);
    building.units?.forEach((unit) => {
      const {
        unitFolders,
        entityCategory: unitCategory,
        unitFolderId,
      } = calculateUnitFolders(
        unit,
        { buildingCategory, buildingName: building.name, buildingId: building.id, buildingFolderId },
        allCategories[EntityType.UNIT],
        language,
        technics,
        unitCharges,
        s3Files
      );
      folders.push(...unitFolders);
      entityCategories.push(unitCategory);
      const pageUnits = getUnit(unit.id, units);
      pageUnits?.leases?.forEach((unitLease) => {
        const lease = getLease(unitLease.id, leases);
        if (isNil(lease)) return;
        const { leaseFolders, leaseDocumentCategory } = calculateLeaseFolders(
          lease,
          { unitLease, unitCategory, unitId: unit.id, unitFolderId, unitName: unit.name },
          {
            buildingCategory,
            buildingName: building.name,
            buildingId: building.id,
            buildingFolderId,
          },
          allCategories[EntityType.LEASE],
          language,
          technics,
          invoices,
          s3Files
        );
        folders.push(...leaseFolders);
        entityCategories.push(leaseDocumentCategory);
      });
    });
    folders.push(...buildingFolders);
  }

  return { folders, entityCategories };
};

const getUnit = (unitId: string, units: Unit[]): Unit | undefined => {
  return units.find((unit) => unit.id === unitId);
};

const getLease = (unitLeaseId: string, leases: Lease[]): Lease | undefined => {
  return leases.find((lease) => lease.units?.some((unitLease) => unitLease.id === unitLeaseId));
};

export const calculateEntityFolders = (
  id: string,
  type: EntityType,
  categories: DocumentCategoryWithoutReadIdAndClientId[],
  s3Files: DocumentModel[],
  contactEntityFolders: DocumentModel[],
  contactCategories: DocumentCategoryWithoutReadIdAndClientId[],
  language: string | null,
  contacts: Contact[]
) => {
  if (type !== EntityType.CONTACT) {
    return setFolderList(
      [],
      s3Files,
      categories,
      language,
      undefined,
      ROOT_FOLDER_ID,
      undefined,
      undefined,
      undefined,
      ROOT_PARENT_FOLDER_ID
    );
  }

  const foldersToAdd: FolderModel[] = [];
  const contact = contacts.find((stateContact) => stateContact.id === id);
  if (!isNil(contact)) {
    const contactType = contactMostImportantType(contact);
    if (contactType === ContactType.JOINT_OWNERSHIP) {
      const jointOwnershipFolders = calculateJointOwnershipFolders(
        contact,
        contacts,
        contactEntityFolders,
        contactCategories,
        language
      );
      if (!isNil(jointOwnershipFolders)) {
        foldersToAdd.push(...jointOwnershipFolders);
      }
    }
  }
  const folderListToAdd = setFolderList(
    [],
    s3Files,
    categories,
    language,
    undefined,
    'rootFolder',
    undefined,
    undefined,
    undefined,
    ROOT_PARENT_FOLDER_ID
  );
  if (!isNil(folderListToAdd)) {
    foldersToAdd.push(...folderListToAdd);
  }
  return foldersToAdd;
};

function getTechnicsFor(id: string, technics: Technic[]): Technic[] {
  return technics.filter(
    (t) => (t.unitId === id && isNil(t.leaseId)) || (t.buildingId === id && isNil(t.leaseId)) || t.leaseId === id
  );
}

export const calculateUniqueCharges = (
  charges: Charge[] | ChargeDTO[],
  unitOwners: UnitOwner[]
): {
  buildingCharges: ChargesMap[];
  unitCharges: ChargesMap[];
} => {
  //We have a lot of duplicates so the map is going to be the fastest way to get unique values (O(1))
  const uniqueChargesMap = new Map<string, ChargesMap>();
  const uniqueChargesMapUnit = new Map<string, ChargesMap>();

  charges.forEach((charge) => {
    const postings = getChargePostings(charge);
    if (!isNilOrEmpty(postings) && !charge.draftFromOcr) {
      postings.forEach((posting) => {
        const unitOwner = unitOwners.some((owner) => owner.unitId === posting.unitId);
        if (unitOwner && posting?.unitId) {
          const key = `${posting.unitId}-${charge.documentId}`;
          if (!uniqueChargesMapUnit.has(key)) {
            uniqueChargesMapUnit.set(key, {
              entityId: posting.unitId,
              documentId: charge.documentId,
            });
          }
        } else if (posting?.buildingId) {
          const key = `${posting.buildingId}-${charge.documentId}`;
          if (!uniqueChargesMap.has(key)) {
            uniqueChargesMap.set(key, {
              entityId: posting.buildingId,
              documentId: charge.documentId,
            });
          }
        }
      });
    }
  });

  return {
    buildingCharges: Array.from(uniqueChargesMap.values()),
    unitCharges: Array.from(uniqueChargesMapUnit.values()),
  };
};
